<script setup>
import { Card, Heading, Color, Hints, Input, Badge, Preview, Alert } from '@components'
import { ref, defineProps, computed } from 'vue'
const props = defineProps({
    index: {
        type: Number,
        default: 1
    }
})

import DarkModeStore from '@o/store'
const { options, isPro, isLocked } = DarkModeStore()


const presetId = computed(() => props.index)
const realIndex = computed(() => presetId.value - 1)

const preset = computed(() => options.color_presets[realIndex.value] )
const isCustom = computed(() => realIndex.value >= wp_dark_mode_admin_json.predefined_presets.length )
const isDuplicate = computed(() => {
    if (isCustom.value) {
        return options.color_presets.some((p, index) => {
            return index !== realIndex.value && preset && p.name && p.name === options.color_presets[realIndex.value].name
        })
    }

    return false
})

const defaults = computed(() => {
    if( isCustom.value ) return {}
    
    return wp_dark_mode_admin_json.predefined_presets[realIndex.value]
})

</script>

<template>
    <Card borderless transparent class="gap-5">
        <!-- color preset customization  -->
        <Card borderless transparent class="w-full gap-6">
            <Heading bold>
                <div class="flex items-center gap-3">
                    <Row locked :ProBadge="false">{{__('Preset Color Customization', 'wp-dark-mode')}}</Row>
                    <Badge v-if="isPro" color="success">New</Badge> <ProBadge />
                </div>
                <Hints locked>{{__('Customize the selected dark mode preset color in your way', 'wp-dark-mode')}}</Hints>
            </Heading>
            <div class="flex flex-wrap justify-start md:flex-nowrap">
                <Card class="w-full gap-6">
                    <!-- preset name  -->
                    <Card borderless locked v-if="isCustom">
                        <Heading> 
                            <Row inline class="items-center"><Row locked :ProBadge="false">Custom Color Preset Name</Row> <ProBadge text=""/></Row>
                        </Heading>
                        <Input :class="{'focus:ring-red-500' : isDuplicate}" v-model="preset.name" placeholder="Preset name"/>
                        <Alert v-if="isDuplicate" color="warning"> {{__('You already have a preset named', 'wp-dark-mode')}} {{ preset.name }}</Alert>
                    </Card>
                    <!-- body colors  -->
                    <Card borderless locked>
                        <Heading> 
                            <Row inline class="items-center"><Row locked :ProBadge="false">Body Colors</Row> <ProBadge text=""/></Row>
                        </Heading>
                        <div class="mt-2 border-t border-gray-100"></div>
                        <Card borderless transparent class="gap-5" locked :disabled="isLocked">
                            <Color v-model="preset.bg" :default="defaults.bg">
                                <Heading>{{__('Body Background Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.secondary_bg" :default="defaults.secondary_bg">
                                <Heading>{{__('Secondary Background Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                        </Card>
                    </Card>
                    <!-- text and link colors  -->
                    <Card borderless transparent locked>
                        <Heading> 
                            <Row inline class="items-center"><Row locked :ProBadge="false">Text & Link Colors</Row> <ProBadge text=""/></Row>
                        </Heading>
                        <div class="mt-2 border-t border-gray-100"></div>
                        <Card borderless transparent class="gap-5" :disabled="isLocked">
                            <Color v-model="preset.text" :default="defaults.text">
                                <Heading>{{__('Text Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.link" :default="defaults.link">
                                <Heading>{{__('Link Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.link_hover" :default="defaults.link_hover">
                                <Heading>{{__('Link Hover Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                        </Card>
                    </Card>
                    <!-- input field colors  -->
                    <Card borderless transparent locked>
                        <Heading> 
                            <Row inline class="items-center"><Row locked :ProBadge="false">Input Field Colors</Row> <ProBadge text=""/></Row>
                        </Heading>
                        <div class="mt-2 border-t border-gray-100"></div>
                        <Card borderless transparent class="gap-5" :disabled="isLocked">
                            <Color v-model="preset.input_bg" :default="defaults.input_bg">
                                <Heading>{{__('Background Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.input_text" :default="defaults.input_text">
                                <Heading>r{{__('Input Text Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.input_placeholder" :default="defaults.input_text">
                                <Heading>{{__('Placeholder Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                        </Card>
                    </Card>
                    <!-- button colors  -->
                    <Card borderless transparent locked>
                        <Heading> 
                            <Row inline class="items-center"><Row locked :ProBadge="false">Button Colors </Row> <ProBadge text=""/></Row>
                        </Heading>
                        <div class="mt-2 border-t border-gray-100"></div>
                        <Card borderless transparent class="gap-5" :disabled="isLocked">
                            <Color v-model="preset.button_text" :default="defaults.button_text">
                                <Heading>{{__('Button Text Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.button_hover_text" :default="defaults.button_hover_text">
                                <Heading>{{__('Button Hover Text Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.button_bg" :default="defaults.button_bg">
                                <Heading>{{__('Button Background Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.button_hover_bg" :default="defaults.button_hover_bg">
                                <Heading>{{__('Button Hover Background Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                            <Color v-model="preset.button_border" :default="defaults.button_border">
                                <Heading>{{__('Button Border Color', 'wp-dark-mode')}}</Heading>
                            </Color>
                        </Card>
                    </Card>
                </Card>
                <Card borderless class="p-6 w-fit">
                    <Preview class="sm:sticky sm:top-20" dark :color="preset"/>
                </Card>
            </div>
        </Card>
        <!-- scrollbar colors  -->
        <Card borderless transparent>
            <Heading bold>
                <Row transparent inline class="items-center"><Row transparent locked :ProBadge="false">
                     {{__('Scrollbar Customization', 'wp-dark-mode')}}</Row> <ProBadge/></Row>
                <Hints locked>{{__('Customize the scrollbar of your website in dark mode', 'wp-dark-mode')}}</Hints>
            </Heading>
            <!-- <div class="mt-2 border-t border-gray-100"></div> -->
            <Card class="gap-5" locked>
                <Card borderless transparent :disabled="isLocked">
                    <Toggle v-model="preset.enable_scrollbar">
                        <Heading>{{__('Enable Dark Mode on Scrollbar', 'wp-dark-mode')}}</Heading>
                    </Toggle>
                    <Hints locked>{{__('Enable this option to customize your scrollbar while toggling to dark mode', 'wp-dark-mode')}}</Hints>
                </Card>
                <Card :disabled="isLocked" borderless transparent  class="gap-5" >
                    <Color v-model="preset.scrollbar_track" :default="defaults.scrollbar_track">
                        <Heading>{{__('Track Background Color', 'wp-dark-mode')}}</Heading>
                    </Color>
                    <Color v-model="preset.scrollbar_thumb" :default="defaults.scrollbar_thumb">
                        <Heading>{{__('Thumb Background Color', 'wp-dark-mode')}}</Heading>
                    </Color>
                </Card>
             
               
                
            </Card>
        </Card>
    </Card>
</template>